import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { json } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import { useProfile } from '../context/ProfileContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Library, Layers, UserMinus, Check, X, Users } from 'lucide-react';
import Page from '../ui/Page';
import { useToast } from '../ui/Toast';
import PageStack from '../ui/PageStack';
import ActionBar from '../ui/ActionBar';
import FilterStrip from '../ui/FilterStrip';
import PageSettingsPopover from '../ui/PageSettingsPopover';
import SchemaModal from '../ui/SchemaModal';
import ConfirmDialog from '../ui/ConfirmDialog';
import SectionHeader from '../ui/SectionHeader';
import List from '../ui/List';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import Skeleton from '../ui/Skeleton';
import ShowMore from '../ui/ShowMore';
import Input from '../ui/Input';
import './FriendsPage.css';

const FRIENDS_LIMIT = 5;

const RADIUS_OPTIONS = [
  { value: '10', label: '10 km' },
  { value: '25', label: '25 km' },
  { value: '50', label: '50 km' },
  { value: '100', label: '100 km' },
  { value: '200', label: '200 km' },
];

// Confirm-Konfig je Aktion. {name} wird zur Laufzeit ersetzt.
const CONFIRMS = {
  'send-request': {
    title: 'Freundschaftsanfrage senden?',
    description: 'An {name} wird eine Anfrage geschickt.',
    confirmLabel: 'Senden',
    tone: 'default',
  },
  'remove-friend': {
    title: 'Freund entfernen?',
    description: '{name} wird aus deinen Freunden entfernt.',
    confirmLabel: 'Entfernen',
    tone: 'danger',
  },
  'decline-request': {
    title: 'Anfrage ablehnen?',
    description: 'Die Anfrage von {name} wird abgelehnt.',
    confirmLabel: 'Ablehnen',
    tone: 'danger',
  },
};

// Distanz-Label aus den API-Feldern (distanceApprox / distanceKm).
function distanceLabel(u) {
  if (u?.distanceKm == null) return null;
  return u.distanceApprox ? '<5 km' : `~${u.distanceKm} km`;
}

const initial = (name) => (name?.trim()?.[0] || '?').toUpperCase();

const FriendsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setViewedUser } = useProfile();
  const toast = useToast();

  // FilterStrip (filtert eigene Freundesliste) + Sortierung
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('name'); // 'name' | 'distance'
  const [sortDir, setSortDir] = useState('asc');       // 'asc' | 'desc'
  const [friendsExpanded, setFriendsExpanded] = useState(false);

  // Nähe
  const [nearbyRadius, setNearbyRadius] = useState(50);

  // Standort-Einstellungen (Gear-Popover)
  const [plzInput, setPlzInput] = useState('');
  const [plzError, setPlzError] = useState('');

  // „Freund hinzufügen"-Modal
  const [addOpen, setAddOpen] = useState(false);
  const [modalSearch, setModalSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Confirm: { type, name, payload }
  const [confirmAction, setConfirmAction] = useState(null);
  const confirm = CONFIRMS[confirmAction?.type];
  const ask = (type, name, payload) => setConfirmAction({ type, name, payload });

  // ─── Queries ───
  const { data: preferencesData } = useQuery({
    queryKey: ['preferences'],
    queryFn: async () => {
      const data = await json('/api/me/preferences');
      return data.preferences;
    },
  });

  React.useEffect(() => {
    if (preferencesData?.postalCode != null) {
      setPlzInput(preferencesData.postalCode || '');
    }
  }, [preferencesData?.postalCode]);

  const { data: friends = [], isLoading: friendsLoading } = useQuery({
    queryKey: ['friends'],
    queryFn: () => json('/api/relationships/friends'),
  });

  const { data: pendingRequests = [] } = useQuery({
    queryKey: ['pendingRequests'],
    queryFn: () => json('/api/relationships/pending'),
  });

  const { data: sentRequests = [] } = useQuery({
    queryKey: ['sentRequests'],
    queryFn: () => json('/api/relationships/sent'),
  });

  const isNearbyEnabled = !!preferencesData?.postalCode && !!preferencesData?.nearbyVisible;
  const {
    data: nearbyUsers = [],
    isLoading: nearbyLoading,
    isError: nearbyError,
  } = useQuery({
    queryKey: ['nearbyUsers', nearbyRadius],
    queryFn: () => json(`/api/users/nearby?radius=${nearbyRadius}&limit=20`),
    enabled: isNearbyEnabled,
  });

  // ─── Mutations ───
  const saveLocationMutation = useMutation({
    mutationFn: (update) => json('/api/me/preferences', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    }),
    onSuccess: (data) => {
      queryClient.setQueryData(['preferences'], data.preferences);
      queryClient.invalidateQueries(['nearbyUsers']);
      setPlzError('');
    },
    meta: { handlesError: true },
    onError: (error) => setPlzError(errorMessage(error, 'Standort konnte nicht gespeichert werden.')),
  });

  const sendRequestMutation = useMutation({
    mutationFn: (addresseeId) => json('/api/relationships/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addresseeId }),
    }),
    onSuccess: (_data, addresseeId) => {
      queryClient.invalidateQueries(['sentRequests']);
      queryClient.invalidateQueries(['nearbyUsers']);
      setSearchResults((prev) =>
        prev.map((u) => (u.id === addresseeId ? { ...u, relationshipStatus: 'pending' } : u))
      );
      toast.push({ tone: 'success', message: 'Freundschaftsanfrage gesendet.' });
    },
    meta: { handlesError: true },
    onError: (error) => toast.push({ tone: 'error', message: errorMessage(error, 'Freundschaftsanfrage konnte nicht gesendet werden.') }),
  });

  const acceptRequestMutation = useMutation({
    mutationFn: (relationshipId) => json(`/api/relationships/${relationshipId}/accept`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['friends']);
      queryClient.invalidateQueries(['pendingRequests']);
      toast.push({ tone: 'success', message: 'Freundschaftsanfrage angenommen.' });
    },
    meta: { handlesError: true },
    onError: (error) => toast.push({ tone: 'error', message: errorMessage(error, 'Anfrage konnte nicht angenommen werden.') }),
  });

  const declineRequestMutation = useMutation({
    mutationFn: (relationshipId) => json(`/api/relationships/${relationshipId}/decline`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['pendingRequests']);
      toast.push({ tone: 'info', message: 'Freundschaftsanfrage abgelehnt.' });
    },
    meta: { handlesError: true },
    onError: (error) => toast.push({ tone: 'error', message: errorMessage(error, 'Anfrage konnte nicht abgelehnt werden.') }),
  });

  const removeFriendMutation = useMutation({
    mutationFn: (relationshipId) => json(`/api/relationships/${relationshipId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['friends']);
      toast.push({ tone: 'info', message: 'Freund entfernt.' });
    },
    meta: { handlesError: true },
    onError: (error) => toast.push({ tone: 'error', message: errorMessage(error, 'Freund konnte nicht entfernt werden.') }),
  });

  // ─── Confirm-Ausführung ───
  const handleConfirm = () => {
    if (!confirmAction) return;
    const { type, payload } = confirmAction;
    if (type === 'remove-friend') removeFriendMutation.mutate(payload);
    else if (type === 'decline-request') declineRequestMutation.mutate(payload);
    else if (type === 'send-request') sendRequestMutation.mutate(payload);
    setConfirmAction(null);
  };

  // ─── Standort ───
  const commitPlz = () => {
    const val = plzInput.trim();
    if (val === (preferencesData?.postalCode || '')) return; // nichts geändert
    if (val.length !== 5) {
      setPlzError('Bitte eine gültige 5-stellige PLZ eingeben.');
      return;
    }
    setPlzError('');
    saveLocationMutation.mutate({ postalCode: val });
  };
  const clearPlz = () => {
    setPlzInput('');
    setPlzError('');
    saveLocationMutation.mutate({ postalCode: null });
  };

  // ─── Suche (Modal) ───
  const handleSearch = async () => {
    if (modalSearch.trim().length < 2) return;
    setIsSearching(true);
    try {
      setSearchResults(await json(`/api/users/search?q=${encodeURIComponent(modalSearch)}`));
    } catch (error) {
      console.error('Fehler bei User-Suche:', error);
      toast.push({ tone: 'error', message: errorMessage(error, 'Fehler bei der User-Suche.') });
    } finally {
      setIsSearching(false);
    }
  };
  const searchTooShort = modalSearch.trim().length > 0 && modalSearch.trim().length < 2;

  // ─── Navigation ───
  const handleViewFriend = (friend, view = 'decks') => {
    setViewedUser(friend.friendId, friend.friendDisplayName);
    navigate(`/${view}`);
  };

  // ─── Filter + Sortierung der eigenen Freundesliste ───
  const sortedFriends = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? friends.filter((f) => f.friendDisplayName?.toLowerCase().includes(q))
      : [...friends];
    list.sort((a, b) => {
      if (sortField === 'distance') {
        const da = a.distanceKm == null ? Infinity : a.distanceKm;
        const db = b.distanceKm == null ? Infinity : b.distanceKm;
        if (da === Infinity && db === Infinity) return 0;
        if (da === Infinity) return 1;   // ohne Distanz immer ans Ende
        if (db === Infinity) return -1;
        const cmp = da - db;
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const cmp = (a.friendDisplayName || '').localeCompare(b.friendDisplayName || '');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [friends, search, sortField, sortDir]);

  const visibleFriends = friendsExpanded ? sortedFriends : sortedFriends.slice(0, FRIENDS_LIMIT);
  const hasMoreFriends = sortedFriends.length > FRIENDS_LIMIT;

  const requestsCount = pendingRequests.length + sentRequests.length;
  const hasRequests = requestsCount > 0;

  return (
    <Page>
      <ActionBar
        left={
          <>
            <ActionBar.Stat id="freunde" value={friends.length} label="Freunde" />
            <ActionBar.Stat id="nearby" value={nearbyUsers.length} label="in der Nähe" />
            <ActionBar.Stat id="anfragen" value={requestsCount} label="Anfragen" />
          </>
        }
        right={
          <>
            <PageSettingsPopover hasChanges={!!preferencesData?.postalCode} label="Standort-Einstellungen">
              <PageSettingsPopover.Section label="Standort">
                <PageSettingsPopover.TextInput
                  label="Postleitzahl"
                  value={plzInput}
                  onChange={(v) => { setPlzInput(v.replace(/\D/g, '').slice(0, 5)); setPlzError(''); }}
                  width="90px"
                  error={plzError}
                  onClear={preferencesData?.postalCode ? clearPlz : undefined}
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 5,
                    onBlur: commitPlz,
                    onKeyDown: (e) => { if (e.key === 'Enter') commitPlz(); },
                  }}
                />
                <PageSettingsPopover.Select
                  label="Suchradius"
                  value={String(nearbyRadius)}
                  onChange={(v) => setNearbyRadius(Number(v))}
                  options={RADIUS_OPTIONS}
                />
              </PageSettingsPopover.Section>
              <PageSettingsPopover.Section label="Sichtbarkeit">
                <PageSettingsPopover.Toggle
                  checked={!!preferencesData?.nearbyVisible}
                  disabled={!preferencesData?.postalCode}
                  onChange={() => saveLocationMutation.mutate({ nearbyVisible: !preferencesData?.nearbyVisible })}
                  label="Auffindbar in der Nähe"
                  description={preferencesData?.postalCode
                    ? 'Andere Spieler im Umkreis können dich finden und Anfragen senden. Deine Postleitzahl wird niemals angezeigt. Andere Spieler sehen nur eine ungefähre Entfernung.'
                    : 'Andere Spieler im Umkreis können dich finden und Anfragen senden. Gib eine Postleitzahl ein, um die Suche aktivieren zu können. Deine Postleitzahl wird nicht angezeigt. Andere sehen nur eine ungefähre Entfernung.'}
                />
                <PageSettingsPopover.Toggle
                  checked={!!preferencesData?.cardsPublicVisible}
                  onChange={() => saveLocationMutation.mutate({ cardsPublicVisible: !preferencesData?.cardsPublicVisible })}
                  label="Sammlung öffentlich"
                  description="Bald verfügbar — Sammlung wird für Freunde sichtbar."
                />
              </PageSettingsPopover.Section>
            </PageSettingsPopover>
            <ActionBar.Action
              icon={Plus}
              label="Freund hinzufügen"
              variant="primary"
              onClick={() => setAddOpen(true)}
            />
          </>
        }
      />

      <FilterStrip>
        <FilterStrip.Search value={search} onChange={setSearch} placeholder="Freunde filtern…" />
        <FilterStrip.RightGroup>
          <FilterStrip.Item id="sortieren">
            <FilterStrip.Group label="Sortieren">
              <FilterStrip.PillMenu
                mode="single"
                options={[
                  { value: 'name', label: 'Name' },
                  { value: 'distance', label: 'Distanz' },
                ]}
                value={sortField}
                onChange={setSortField}
              />
              <FilterStrip.SortDirection
                direction={sortDir}
                onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              />
            </FilterStrip.Group>
          </FilterStrip.Item>
        </FilterStrip.RightGroup>
      </FilterStrip>

      <PageStack>
        {/* ─── Meine Freunde ─── */}
        <section>
          <SectionHeader>Meine Freunde</SectionHeader>
          {friendsLoading ? (
            <List>
              {[0, 1, 2].map((i) => (
                <List.Item key={i}>
                  <List.Avatar><Skeleton width="20px" height="20px" radius="pill" /></List.Avatar>
                  <List.Main>
                    <List.Name><Skeleton width="160px" /></List.Name>
                    <List.Meta><Skeleton width="60px" /></List.Meta>
                  </List.Main>
                </List.Item>
              ))}
            </List>
          ) : sortedFriends.length === 0 ? (
            <EmptyState
              icon={Users}
              size="compact"
              variant={search ? 'filter' : 'standard'}
              title={search ? 'Keine Freunde gefunden' : 'Noch keine Freunde'}
              description={search ? undefined : 'Füge über „Freund hinzufügen“ deinen ersten Freund hinzu.'}
            />
          ) : (
            <>
              <List>
                {visibleFriends.map((f) => {
                  const dist = distanceLabel(f);
                  return (
                    <List.Item key={f.relationshipId}>
                      <List.Avatar>{initial(f.friendDisplayName)}</List.Avatar>
                      <List.Main>
                        <List.Name>{f.friendDisplayName}</List.Name>
                        {dist && <List.Meta>{dist}</List.Meta>}
                      </List.Main>
                      <List.Actions>
                        <button type="button" className="btn-icon" aria-label={`Sammlung von ${f.friendDisplayName} ansehen`} title="Sammlung ansehen" onClick={() => handleViewFriend(f, 'collection')}>
                          <Library size={16} />
                        </button>
                        <button type="button" className="btn-icon" aria-label={`Decks von ${f.friendDisplayName} ansehen`} title="Decks ansehen" onClick={() => handleViewFriend(f, 'decks')}>
                          <Layers size={16} />
                        </button>
                        <button type="button" className="btn-icon" aria-label={`${f.friendDisplayName} entfernen`} title="Freund entfernen" onClick={() => ask('remove-friend', f.friendDisplayName, f.relationshipId)}>
                          <UserMinus size={16} />
                        </button>
                      </List.Actions>
                    </List.Item>
                  );
                })}
              </List>
              {hasMoreFriends && !friendsExpanded && (
                <ShowMore onClick={() => setFriendsExpanded(true)}>
                  Alle {sortedFriends.length} Freunde anzeigen
                </ShowMore>
              )}
            </>
          )}
        </section>

        {/* ─── In der Nähe ─── */}
        <section>
          <SectionHeader>In der Nähe</SectionHeader>
          {!isNearbyEnabled ? (
            <EmptyState
              icon={Users}
              size="compact"
              title="Spieler in der Nähe finden"
              description="Aktiviere „Auffindbar in der Nähe“ in den Standort-Einstellungen (Zahnrad oben), um Spieler im Umkreis zu sehen."
            />
          ) : nearbyLoading ? (
            <List>
              {[0, 1].map((i) => (
                <List.Item key={i}>
                  <List.Avatar><Skeleton width="20px" height="20px" radius="pill" /></List.Avatar>
                  <List.Main><List.Name><Skeleton width="140px" /></List.Name></List.Main>
                </List.Item>
              ))}
            </List>
          ) : nearbyError ? (
            <EmptyState icon={Users} size="compact" variant="filter" title="Spieler in der Nähe konnten nicht geladen werden" />
          ) : nearbyUsers.length === 0 ? (
            <EmptyState icon={Users} size="compact" variant="filter" title={`Niemand im Umkreis von ${nearbyRadius} km`} />
          ) : (
            <List>
              {nearbyUsers.map((p) => {
                const dist = distanceLabel(p);
                return (
                  <List.Item key={p.id}>
                    <List.Avatar>{initial(p.displayName)}</List.Avatar>
                    <List.Main>
                      <List.Name>{p.displayName}</List.Name>
                      {dist && <List.Meta>{dist}</List.Meta>}
                    </List.Main>
                    <List.Actions>
                      {p.relationshipStatus === 'accepted' ? (
                        <Badge tone="accent">Freund</Badge>
                      ) : p.relationshipStatus === 'pending' ? (
                        <Badge tone="pending">Angefragt</Badge>
                      ) : (
                        <button type="button" className="btn-icon" aria-label={`${p.displayName} Anfrage senden`} title="Anfrage senden" onClick={() => ask('send-request', p.displayName, p.id)}>
                          <Plus size={16} />
                        </button>
                      )}
                    </List.Actions>
                  </List.Item>
                );
              })}
            </List>
          )}
        </section>

        {/* ─── Anfragen ─── */}
        {hasRequests && (
          <section>
            <SectionHeader>Anfragen</SectionHeader>

            {pendingRequests.length > 0 && (
              <>
                <SectionHeader size="sm">Eingehend</SectionHeader>
                <List>
                  {pendingRequests.map((r) => (
                    <List.Item key={r.relationshipId}>
                      <List.Avatar>{initial(r.requesterDisplayName)}</List.Avatar>
                      <List.Main><List.Name>{r.requesterDisplayName}</List.Name></List.Main>
                      <List.Actions>
                        <button type="button" className="btn-icon" aria-label={`${r.requesterDisplayName} Anfrage ablehnen`} title="Ablehnen" onClick={() => ask('decline-request', r.requesterDisplayName, r.relationshipId)}>
                          <X size={16} />
                        </button>
                        <button type="button" className="btn-icon" aria-label={`${r.requesterDisplayName} Anfrage annehmen`} title="Annehmen" onClick={() => acceptRequestMutation.mutate(r.relationshipId)}>
                          <Check size={16} />
                        </button>
                      </List.Actions>
                    </List.Item>
                  ))}
                </List>
              </>
            )}

            {sentRequests.length > 0 && (
              <>
                <SectionHeader size="sm">Gesendet</SectionHeader>
                <List>
                  {sentRequests.map((r) => (
                    <List.Item key={r.relationshipId}>
                      <List.Avatar>{initial(r.addresseeDisplayName)}</List.Avatar>
                      <List.Main><List.Name>{r.addresseeDisplayName}</List.Name></List.Main>
                      <List.Actions><Badge tone="pending">Ausstehend</Badge></List.Actions>
                    </List.Item>
                  ))}
                </List>
              </>
            )}
          </section>
        )}
      </PageStack>

      {/* ─── „Freund hinzufügen"-Modal ─── */}
      <SchemaModal
        open={addOpen}
        onOpenChange={setAddOpen}
        size="md"
        dismissOnBackdrop={false}
        title="Freund hinzufügen"
        description="Suche nach einem Benutzernamen — Treffer kannst du direkt anfragen."
      >
        <div className="friends-add__search">
          <Input
            size="sm"
            type="text"
            value={modalSearch}
            onChange={(e) => setModalSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="z. B. AnnaSchmidt…"
            autoFocus
          />
          <button className="btn-primary" onClick={handleSearch} disabled={isSearching || modalSearch.trim().length < 2}>
            Suchen
          </button>
        </div>
        {searchTooShort && <p className="friends-add__hint">Bitte mindestens 2 Zeichen eingeben.</p>}

        {searchResults.length > 0 && (
          <>
            <SectionHeader size="sm">Ergebnisse</SectionHeader>
            <List>
              {searchResults.map((u) => (
                <List.Item key={u.id}>
                  <List.Avatar>{initial(u.displayName)}</List.Avatar>
                  <List.Main><List.Name>{u.displayName}</List.Name></List.Main>
                  <List.Actions>
                    {u.relationshipStatus === 'accepted' ? (
                      <Badge tone="accent">Freund</Badge>
                    ) : u.relationshipStatus === 'pending' ? (
                      <Badge tone="pending">Angefragt</Badge>
                    ) : (
                      <button type="button" className="btn-icon" aria-label={`${u.displayName} Anfrage senden`} title="Anfrage senden" onClick={() => ask('send-request', u.displayName, u.id)}>
                        <Plus size={16} />
                      </button>
                    )}
                  </List.Actions>
                </List.Item>
              ))}
            </List>
          </>
        )}
      </SchemaModal>

      {/* ─── Bestätigungen ─── */}
      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => { if (!open) setConfirmAction(null); }}
        title={confirm?.title}
        description={confirm?.description?.replace('{name}', confirmAction?.name ?? '')}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone}
        onConfirm={handleConfirm}
      />
    </Page>
  );
};

export default FriendsPage;
